import { MultipartBody } from '@microsoft/kiota-abstractions';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { FilterModel, IServerSideGetRowsRequest, SortModelItem } from 'ag-grid-enterprise';
import type {
  AgGridClientQuery,
  Fare,
  FareCreateRequest,
  FareUpdateRequest,
  FileStreamResult,
  PagingResponseOfFare,
  StagingFare,
} from '@/api/client/models/index.ts';
import type { DatasourceResponse } from '@/models/DatasourceResponse.ts';
import { InsertSelectedFareRequestBuilderRequestsMetadata } from '@/api/client/stagingFare/insertSelectedFare/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { createFileDownloadResponseHandlerOption, downloadFile } from '@/util/file-download.ts';
import { mapFilterModelToApi, mapSortModelToApi } from '@/util/grid-to-api-mappers.ts';

type UploadFile = Blob & {
  name?: string;
  type?: string;
};

function ensureInsertSelectedFareRequestBodySerialization(): void {
  // Kiota omits this marker for scalar-array request bodies, so the proxy skips the JSON body.
  InsertSelectedFareRequestBuilderRequestsMetadata.post!.requestBodySerializer ??= 'number';
}

export const useFaresStore = defineStore('useFares', () => {
  const apiClient = useApiClient();

  const added = ref<Fare | null>(null);
  const loading = ref<boolean>(false);
  const showOnlyActive = ref<boolean>(true);

  const fieldMap: Record<string, string> = {
    origin: 'fare.origin',
    destination: 'fare.destination',
    operatingAirlineId: 'fare.operatingairlineid',
    seasonId: 'fare.seasonId',
    flightNumber: 'fare.flightnumber',
  };

  async function add(data: FareCreateRequest): Promise<Fare | undefined> {
    const response = await apiClient.fare.post(data);
    added.value = response ?? null;
    return response;
  }

  async function toggle({ id, active }: { id: number; active: boolean }): Promise<ArrayBuffer | undefined> {
    return await apiClient.fare.byId(id).byIsActive(active).post();
  }

  async function update(data: FareUpdateRequest): Promise<ArrayBuffer | undefined> {
    return await apiClient.fare.put(data);
  }

  async function upload(input: UploadFile): Promise<StagingFare[]> {
    const multipartBody = new MultipartBody();
    multipartBody.addOrReplacePart(
      'formFile',
      input.type || 'text/csv',
      await input.arrayBuffer(),
      undefined,
      input.name || 'fares.csv'
    );

    const response = await apiClient.stagingFare.createFromCsv.post(multipartBody);
    return response ?? [];
  }

  async function commit(data: number[]): Promise<Fare[]> {
    ensureInsertSelectedFareRequestBodySerialization();
    const response = await apiClient.stagingFare.insertSelectedFare.post(data.map(entry => Number(entry)));
    return response ?? [];
  }

  async function get(request: IServerSideGetRowsRequest): Promise<DatasourceResponse<Fare>> {
    loading.value = true;

    const { startRow, endRow, sortModel, filterModel } = request;
    const requestParameters: AgGridClientQuery = {
      startRow,
      endRow,
      sortModel: mapSortModelToApi(sortModel, fieldMap),
      filterModel: mapFilterModelToApi((filterModel ?? {}) as FilterModel, fieldMap),
    };

    try {
      const response: PagingResponseOfFare | undefined = await apiClient.fare.getPath.post(requestParameters, {
        queryParameters: {
          isActive: showOnlyActive.value,
        },
      });

      return {
        total: response?.total ?? 0,
        rows: response?.rows ?? [],
      };
    } finally {
      loading.value = false;
    }
  }

  async function exportCsv(filterModel: Record<string, FilterModel>, sortModel: SortModelItem[]): Promise<FileStreamResult | undefined> {
    const requestParameters: AgGridClientQuery = {
      sortModel: mapSortModelToApi(sortModel, fieldMap),
      filterModel: mapFilterModelToApi(filterModel, fieldMap),
    };

    const response = await apiClient.fare.createCsv.post(requestParameters, {
      options: [createFileDownloadResponseHandlerOption()],
      queryParameters: {
        isActive: showOnlyActive.value,
      },
    });

    return downloadFile(response, 'Fares.csv');
  }

  return {
    added,
    loading,
    showOnlyActive,
    add,
    get,
    toggle,
    update,
    upload,
    commit,
    exportCsv,
  };
});
